import React, { useEffect, useMemo, useState } from "react";
import { Search, X, SlidersHorizontal, ArrowLeft } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useLanguage } from "@/context/LanguageContext";

const EMPTY = { items: [], macros: [], authors: [], spirits: [], types: [], themes: [], languages: [], series: [] };

const norm = (s) =>
  (s || "").normalize("NFKD").replace(/[̀-ͯ]/g, "").toLowerCase();

const Select = ({ testid, value, onChange, placeholder, options }) => {
  const label = `${placeholder} (${options.length})`;
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="h-10 px-3 bg-secondary/40 rounded-sm border border-border text-sm outline-none focus:border-primary transition-colors w-full min-w-0"
    >
      <option value="">{label}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
};

const Tag = ({ children }) => (
  <span className="font-sans text-[10px] uppercase tracking-[0.12em] text-muted-foreground/80 border border-border/70 rounded-full px-2.5 py-0.5">
    {children}
  </span>
);

const Field = ({ label, children }) => (
  <div>
    <dt className="font-sans text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</dt>
    <dd className="mt-1 font-sans text-sm text-foreground">{children}</dd>
  </div>
);

export const CatalogModal = ({ open, onOpenChange }) => {
  const { t } = useLanguage();
  const [data, setData] = useState(EMPTY);
  const [query, setQuery] = useState("");
  const [macro, setMacro] = useState("");
  const [author, setAuthor] = useState("");
  const [spirit, setSpirit] = useState("");
  const [type, setType] = useState("");
  const [theme, setTheme] = useState("");
  const [serie, setSerie] = useState("");
  const [showMore, setShowMore] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!open || data.items.length) return;
    fetch("/catalog.json").then((r) => r.json()).then(setData);
  }, [open, data.items.length]);

  useEffect(() => { if (!open) setSelected(null); }, [open]);

  const filtered = useMemo(() => {
    const q = norm(query.trim());
    return data.items.filter((it) => {
      if (macro && !it.macros.includes(macro)) return false;
      if (author && !it.authors.includes(author)) return false;
      if (spirit && !it.spirits.includes(spirit)) return false;
      if (type && !it.types.includes(type)) return false;
      if (theme && !it.themes.includes(theme)) return false;
      if (serie && !it.series.includes(serie)) return false;
      if (!q) return true;
      const hay = [
        it.title, it.cde, it.vol, it.authors_original, it.spirits_original,
        ...it.authors, ...it.spirits, ...it.themes, ...it.classifications_original,
        ...it.macros, ...it.types, ...it.series,
      ].map(norm).join(" ");
      return hay.includes(q);
    });
  }, [data.items, query, macro, author, spirit, type, theme, serie]);

  const hasFilters = query || macro || author || spirit || type || theme || serie;
  const moreCount = [type, theme, serie].filter(Boolean).length;
  const clearAll = () => {
    setQuery(""); setMacro(""); setAuthor(""); setSpirit("");
    setType(""); setTheme(""); setSerie("");
  };
  const pick = (it) => setSelected(it);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="catalog-modal"
        className="max-w-6xl w-[95vw] max-h-[90vh] p-0 overflow-hidden bg-background text-foreground flex flex-col"
      >
        {selected ? (
          /* ---------- FICHA DA OBRA ---------- */
          <div className="flex flex-col min-h-0" data-testid="catalog-detail">
            <div className="px-6 md:px-10 pt-6 pb-5 border-b border-border">
              <button
                type="button"
                onClick={() => setSelected(null)}
                data-testid="catalog-detail-back"
                className="inline-flex items-center gap-2 font-sans text-[11px] uppercase tracking-[0.18em] text-primary hover:text-primary/70 transition-colors"
              >
                <ArrowLeft size={13} strokeWidth={1.5} /> {t("library.back")}
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 md:px-10 py-8">
              <h3 className="font-serif text-2xl md:text-4xl text-foreground leading-tight">
                {selected.title}
              </h3>
              <div className="mt-3 font-sans text-sm text-muted-foreground space-y-0.5">
                {selected.authors.length > 0 && <p>{selected.authors.join(" · ")}</p>}
                {selected.spirits.length > 0 && (
                  <p className="italic">↳ {t("library.spirit")}: {selected.spirits.join(" · ")}</p>
                )}
              </div>

              <dl className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-5">
                <Field label="CDE">{selected.cde || "—"}</Field>
                <Field label={t("library.volume")}>{selected.vol || "—"}</Field>
                {selected.copies > 1 && (
                  <Field label={t("library.copies")}>{selected.copies}</Field>
                )}
                {selected.series.length > 0 && (
                  <Field label={t("library.collection")}>{selected.series.join(" · ")}</Field>
                )}
                {selected.languages.length > 0 && (
                  <Field label={t("library.language")}>{selected.languages.join(" · ")}</Field>
                )}
                {selected.types.length > 0 && (
                  <Field label={t("library.type")}>{selected.types.join(" · ")}</Field>
                )}
              </dl>

              {selected.macros.length > 0 && (
                <div className="mt-8">
                  <p className="font-sans text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                    {t("library.category")}
                  </p>
                  <div className="mt-2.5 flex flex-wrap gap-1.5">
                    {selected.macros.map((m) => (
                      <span
                        key={m}
                        className="font-sans text-[10px] uppercase tracking-[0.12em] rounded-full border border-primary/40 text-primary px-2.5 py-0.5"
                      >
                        {m}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {selected.themes.length > 0 && (
                <div className="mt-8" data-testid="catalog-detail-themes">
                  <p className="font-sans text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                    {t("library.relatedThemes")}
                  </p>
                  <div className="mt-2.5 flex flex-wrap gap-1.5">
                    {selected.themes.map((th) => <Tag key={th}>{th}</Tag>)}
                  </div>
                </div>
              )}

              <div className="mt-10 pt-6 border-t border-border/60" data-testid="catalog-detail-original">
                <p className="font-sans text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  {t("library.originalClassifications")}
                </p>
                <div className="mt-3 font-sans text-xs text-muted-foreground space-y-1.5">
                  {selected.classifications_original.map((c, i) => (
                    <p key={i}><span className="tabular-nums text-muted-foreground/60">{i + 1}ª</span> — {c}</p>
                  ))}
                  {(selected.authors_original || selected.spirits_original) && (
                    <p className="pt-2 text-muted-foreground/70">
                      {[selected.authors_original, selected.spirits_original].filter(Boolean).join(" · ")}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* ---------- LISTA + FILTROS ---------- */
          <>
            <div className="px-6 md:px-10 pt-8 pb-4 border-b border-border">
              <DialogTitle className="font-serif text-3xl md:text-4xl text-foreground">
                {t("library.catalogTitle")}
              </DialogTitle>
              <p className="font-sans text-sm text-muted-foreground mt-2" data-testid="catalog-count">
                {data.items.length > 0 && (
                  <>
                    {filtered.length} / {data.items.length} · {t("library.catalogSubtitle")}
                    {data.totalCopies > data.items.length && <> · {data.totalCopies} {t("library.copies")}</>}
                  </>
                )}
              </p>

              <div className="mt-5 grid grid-cols-2 md:grid-cols-[minmax(200px,1fr)_repeat(3,minmax(0,190px))_auto] gap-3">
                <div className="relative col-span-2 md:col-span-1">
                  <Search size={14} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    autoFocus
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t("library.searchPlaceholder")}
                    data-testid="catalog-search"
                    className="w-full h-10 pl-9 pr-3 bg-secondary/40 rounded-sm border border-border text-sm outline-none focus:border-primary transition-colors"
                  />
                </div>
                <Select testid="catalog-category" value={macro} onChange={setMacro}
                  placeholder={t("library.allCategories")} options={data.macros} />
                <Select testid="catalog-author" value={author} onChange={setAuthor}
                  placeholder={t("library.allAuthors")} options={data.authors} />
                <Select testid="catalog-spirit" value={spirit} onChange={setSpirit}
                  placeholder={t("library.allSpirits")} options={data.spirits} />
                <button
                  type="button"
                  onClick={() => setShowMore((v) => !v)}
                  data-testid="catalog-more-filters"
                  className={`h-10 px-3 inline-flex items-center justify-center gap-2 rounded-sm border text-sm transition-colors col-span-2 md:col-span-1 ${
                    showMore || moreCount
                      ? "border-primary text-primary"
                      : "border-border text-muted-foreground hover:border-primary hover:text-primary"
                  }`}
                >
                  <SlidersHorizontal size={14} strokeWidth={1.5} />
                  {showMore ? t("library.fewerFilters") : t("library.moreFilters")}
                  {moreCount > 0 && <span className="tabular-nums text-xs">({moreCount})</span>}
                </button>
              </div>

              {showMore && (
                <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="catalog-more-panel">
                  <Select testid="catalog-type" value={type} onChange={setType}
                    placeholder={t("library.allTypes")} options={data.types} />
                  <Select testid="catalog-theme" value={theme} onChange={setTheme}
                    placeholder={t("library.allThemes")} options={data.themes} />
                  <Select testid="catalog-series" value={serie} onChange={setSerie}
                    placeholder={t("library.allCollections")} options={data.series} />
                </div>
              )}

              {hasFilters && (
                <button
                  type="button"
                  onClick={clearAll}
                  className="mt-3 inline-flex items-center gap-1.5 font-sans text-[11px] uppercase tracking-[0.18em] text-primary hover:text-primary/70"
                  data-testid="catalog-clear"
                >
                  <X size={12} strokeWidth={2} /> {t("library.clearFilters")}
                </button>
              )}
            </div>

            <div className="flex-1 overflow-y-auto">
              <ul>
                {filtered.map((it, i) => (
                  <li key={`${it.cde}-${i}`}>
                    <button
                      type="button"
                      onClick={() => pick(it)}
                      data-testid={`catalog-row-${i}`}
                      className="w-full text-left px-6 md:px-10 py-5 border-b border-border/50 hover:bg-secondary/30 transition-colors"
                    >
                      <div className="flex items-start gap-4 md:gap-6">
                        <span className="shrink-0 w-14 md:w-20 pt-1 font-sans tabular-nums text-xs text-muted-foreground">
                          {it.cde}
                          {it.vol && <span className="block text-muted-foreground/60">v. {it.vol}</span>}
                          {it.copies > 1 && <span className="block text-muted-foreground/60">×{it.copies}</span>}
                        </span>
                        <div className="min-w-0 flex-1">
                          <h4 className="font-serif text-base md:text-lg text-foreground leading-snug">
                            {it.title}
                          </h4>
                          {(it.authors.length > 0 || it.spirits.length > 0) && (
                            <p className="mt-1 font-sans text-xs text-muted-foreground">
                              {it.authors.join(" · ")}
                              {it.spirits.length > 0 && (
                                <span className="italic">
                                  {it.authors.length > 0 ? " · " : ""}↳ {it.spirits.join(" · ")}
                                </span>
                              )}
                            </p>
                          )}
                          {it.themes.length > 0 && (
                            <p className="mt-1.5 font-sans text-[10px] uppercase tracking-[0.14em] text-muted-foreground/85">
                              {it.themes.slice(0, 3).join("  ·  ")}
                            </p>
                          )}
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
                {filtered.length === 0 && data.items.length > 0 && (
                  <li className="text-center py-16 text-muted-foreground font-sans text-sm" data-testid="catalog-empty">
                    {t("library.noResults")}
                  </li>
                )}
              </ul>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
